import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { TrashIcon, CheckCircleIcon, PlayIcon, PauseIcon, VolumeXIcon, Volume2Icon, AlertCircleIcon, ExternalLinkIcon, ShieldAlertIcon, MapPinIcon, ClockIcon, CopyrightIcon, ImageIcon } from 'lucide-react';
export const VideoCard = ({
  id,
  title,
  videoId,
  channelName,
  channelVerified = false,
  views,
  timestamp,
  duration,
  customThumbnail,
  isCustom = false,
  onRemove,
  onTitleUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title || 'Video title');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [embedError, setEmbedError] = useState(false);
  const [errorType, setErrorType] = useState(''); // 'embedding', 'age', 'region', 'copyright', 'unavailable'
  const [isLoading, setIsLoading] = useState(false);
  const [embedAttempted, setEmbedAttempted] = useState(false);
  const inputRef = useRef(null);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  // Check if this is a custom thumbnail-only card (no video)
  const isThumbnailOnly = customThumbnail && videoId === null;
  // More detailed debugging
  useEffect(() => {
    console.log('VideoCard props received:', {
      id,
      title,
      videoId,
      hasCustomThumbnail: Boolean(customThumbnail),
      customThumbnailType: customThumbnail ? customThumbnail.startsWith('data:') ? 'base64' : 'objectURL' : 'none',
      customThumbnailLength: customThumbnail ? customThumbnail.substring(0, 30) + '...' : 'none',
      isThumbnailOnly,
      isCustom
    });
  }, [id, title, videoId, customThumbnail, isThumbnailOnly, isCustom]);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  // Update local state if props change
  useEffect(() => {
    setEditedTitle(title || 'Video title');
  }, [title]);
  const handleTitleClick = () => {
    if (isCustom && onTitleUpdate) {
      setIsEditing(true);
    }
  };
  const handleTitleSave = () => {
    if (isCustom && onTitleUpdate && editedTitle.trim()) {
      onTitleUpdate(id, editedTitle);
      setIsEditing(false);
    } else if (!editedTitle.trim()) {
      setEditedTitle(title || 'Video title');
      setIsEditing(false);
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setEditedTitle(title || 'Video title');
      setIsEditing(false);
    }
  };
  const handleClickOutside = e => {
    if (inputRef.current && !inputRef.current.contains(e.target)) {
      handleTitleSave();
    }
  };
  useEffect(() => {
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);
  // YouTube iframe API error handling
  useEffect(() => {
    const handleYouTubeMessages = event => {
      // Only process messages from YouTube
      if (event.origin !== 'https://www.youtube.com') return;
      try {
        // Try to parse the message data
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        // Check for error events
        if (data.event === 'onError') {
          setEmbedError(true);
          setIsLoading(false);
          // Map YouTube error codes to our error types
          // https://developers.google.com/youtube/iframe_api_reference#Events
          switch (data.info) {
            case 2:
              // Invalid parameter
              setErrorType('unavailable');
              break;
            case 100:
              // Video not found
              setErrorType('unavailable');
              break;
            case 101: // Embedding disabled
            case 150:
              // Embedding disabled
              setErrorType('embedding');
              break;
            default:
              setErrorType('unavailable');
          }
        }
      } catch (e) {
        // If we can't parse the message, just ignore it
      }
    };
    if (!isThumbnailOnly) {
      window.addEventListener('message', handleYouTubeMessages);
      return () => {
        window.removeEventListener('message', handleYouTubeMessages);
      };
    }
  }, [isThumbnailOnly]);
  const togglePlay = () => {
    // If this is a thumbnail-only card, don't try to play
    if (isThumbnailOnly) {
      return;
    }
    setIsPlaying(!isPlaying);
    setEmbedError(false);
    setErrorType('');
    setIsLoading(true);
    setEmbedAttempted(true);
    // Before attempting to play, check if the video exists
    if (!isPlaying) {
      checkVideoAvailability(videoId);
    }
  };
  // Function to check video availability before attempting to play
  const checkVideoAvailability = async videoId => {
    try {
      // Use YouTube's oEmbed endpoint to check if the video exists and is embeddable
      const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      // If the response is not ok, the video might not be available or embeddable
      if (!response.ok) {
        const status = response.status;
        setEmbedError(true);
        setIsLoading(false);
        // Try to determine the error type based on the status code
        if (status === 401) {
          setErrorType('age');
        } else if (status === 403) {
          setErrorType('embedding');
        } else if (status === 404) {
          setErrorType('unavailable');
        } else {
          setErrorType('unavailable');
        }
      }
      // If response is ok, we'll still try to embed, but the iframe might catch other errors
    } catch (error) {
      // Network error or other exception
      console.error('Error checking video availability:', error);
      // Continue with embed attempt, the iframe error handler will catch issues
    }
  };
  const toggleMute = e => {
    e.stopPropagation();
    setIsMuted(!isMuted);
    // If we have an iframe reference, use postMessage to mute/unmute
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        const command = isMuted ? 'unMute' : 'mute';
        iframeRef.current.contentWindow.postMessage(`{"event":"command","func":"${command}","args":""}`, '*');
      } catch (e) {
        console.error('Failed to control YouTube player:', e);
      }
    }
  };
  // Handle iframe load events
  const handleIframeLoad = () => {
    setIsLoading(false);
  };
  const handleIframeError = () => {
    setEmbedError(true);
    setIsLoading(false);
    setErrorType('unavailable');
  };
  // Generate a YouTube thumbnail URL directly from the video ID
  const getYouTubeThumbnail = videoId => {
    if (!videoId) return null;
    // Try maxresdefault first (highest quality)
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };
  // Fallback to hqdefault if maxresdefault fails
  const handleThumbnailError = e => {
    if (videoId) {
      e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    } else {
      e.target.src = 'https://via.placeholder.com/480x360?text=Thumbnail+Error';
    }
  };
  // Second fallback if hqdefault fails
  const handleSecondThumbnailError = e => {
    if (videoId) {
      e.target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    } else {
      e.target.src = 'https://via.placeholder.com/480x360?text=Thumbnail+Error';
    }
  };
  // Check if the video is available after a timeout
  useEffect(() => {
    let timeoutId;
    if (isPlaying && !embedError && !isThumbnailOnly) {
      // Set a timeout to check if the video is still loading after 5 seconds
      timeoutId = setTimeout(() => {
        if (isLoading) {
          setEmbedError(true);
          setErrorType('timeout');
          setIsLoading(false);
        }
      }, 5000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isPlaying, isLoading, embedError, isThumbnailOnly]);
  // Get the appropriate error icon based on error type
  const getErrorIcon = () => {
    switch (errorType) {
      case 'embedding':
        return <CopyrightIcon size={24} color="white" data-id="element-293" />;
      case 'age':
        return <ShieldAlertIcon size={24} color="white" data-id="element-294" />;
      case 'region':
        return <MapPinIcon size={24} color="white" data-id="element-295" />;
      case 'copyright':
        return <CopyrightIcon size={24} color="white" data-id="element-296" />;
      case 'timeout':
        return <ClockIcon size={24} color="white" data-id="element-297" />;
      default:
        return <AlertCircleIcon size={24} color="white" data-id="element-298" />;
    }
  };
  // Get error message based on error type
  const getErrorMessage = () => {
    switch (errorType) {
      case 'embedding':
        return 'This video cannot be embedded. The creator has disabled embedding.';
      case 'age':
        return 'This video is age-restricted and cannot be embedded.';
      case 'region':
        return 'This video is not available in your region.';
      case 'copyright':
        return 'This video contains content that restricts embedding.';
      case 'timeout':
        return 'The video took too long to load. It may be unavailable.';
      default:
        return 'This video cannot be embedded. It may be unavailable or removed.';
    }
  };
  // Render YouTube video embed or thumbnail preview
  const renderVideoContent = () => {
    // CASE 1: This is a custom thumbnail-only card (no video)
    if (isThumbnailOnly) {
      console.log('RENDER PATH: Custom thumbnail-only for:', id, 'with title:', title);
      return <>
          <img src={customThumbnail} alt={title} className="w-full h-full object-cover" loading="lazy" onError={e => {
          console.error('Custom thumbnail failed to load:', e);
          e.target.onerror = null;
          e.target.src = 'https://via.placeholder.com/480x360?text=Thumbnail+Error';
        }} data-id="element-299" />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity duration-200" data-id="element-300">
            <div className="bg-[#272727] rounded-full p-4" data-id="element-301">
              <ImageIcon size={24} className="text-white" data-id="element-302" />
            </div>
          </div>
          {duration && <div className="absolute bottom-1 right-1 bg-black bg-opacity-80 px-1 py-0.5 rounded text-xs font-medium" data-id="element-303">
              {duration}
            </div>}
        </>;
    }
    // CASE 2: This is a regular video but no videoId is provided
    if (!videoId) {
      console.log('RENDER PATH: No video ID for:', id, 'with customThumbnail?', Boolean(customThumbnail));
      return <div className="w-full h-full bg-gray-800 flex items-center justify-center" data-id="element-304">
          <div className="text-center" data-id="element-305">
            <div className="bg-red-600 rounded-xl p-3 mb-2" data-id="element-306">
              <PlayIcon size={24} color="white" data-id="element-307" />
            </div>
            <span className="text-gray-400 text-xs" data-id="element-308">No video ID provided</span>
          </div>
        </div>;
    }
    // CASE 3: This is a regular video with videoId
    if (isPlaying) {
      if (embedError) {
        // Show error message if embedding fails
        return <div className="w-full h-full bg-gray-800 flex items-center justify-center" data-id="element-309">
            <div className="text-center px-4" data-id="element-310">
              <div className="bg-red-600 rounded-full p-3 mb-2 mx-auto w-12 h-12 flex items-center justify-center" data-id="element-311">
                {getErrorIcon()}
              </div>
              <h3 className="text-white text-sm font-medium mb-1" data-id="element-312">
                {errorType === 'embedding' ? 'Embedding Disabled' : 'Video Unavailable'}
              </h3>
              <p className="text-gray-400 text-xs" data-id="element-313">{getErrorMessage()}</p>
              <a href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block bg-red-600 hover:bg-red-700 text-white py-1 px-3 rounded-full text-xs" onClick={e => e.stopPropagation()} data-id="element-314">
                <span className="flex items-center" data-id="element-315">
                  Watch on YouTube
                  <ExternalLinkIcon size={12} className="ml-1" data-id="element-316" />
                </span>
              </a>
              <button onClick={() => setIsPlaying(false)} className="mt-2 inline-block bg-gray-700 hover:bg-gray-600 text-white py-1 px-3 rounded-full text-xs ml-2" data-id="element-317">
                Back to Preview
              </button>
            </div>
          </div>;
      }
      if (isLoading) {
        return <div className="w-full h-full bg-gray-800 flex items-center justify-center" data-id="element-318">
            <div className="text-center" data-id="element-319">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mb-2" data-id="element-320"></div>
              <span className="text-gray-400 text-xs" data-id="element-321">Loading video...</span>
            </div>
          </div>;
      }
      // YouTube embed with enhanced parameters and origin
      return <>
          <iframe ref={iframeRef} src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&mute=1&controls=1&modestbranding=1&rel=0&playsinline=1&fs=1&iv_load_policy=3&origin=${encodeURIComponent(window.location.origin)}`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full border-0" onLoad={handleIframeLoad} onError={handleIframeError} data-id="element-322" />
        </>;
    } else {
      // Custom thumbnail or YouTube thumbnail preview with play button overlay
      return <>
          {customThumbnail ? <img src={customThumbnail} alt={title} className="w-full h-full object-cover" loading="lazy" onError={e => {
          e.target.onerror = null;
          e.target.src = 'https://via.placeholder.com/480x360?text=Thumbnail+Error';
        }} data-id="element-323" /> : <img src={getYouTubeThumbnail(videoId)} alt={title} className="w-full h-full object-cover" loading="lazy" onError={handleThumbnailError} onLoad={e => e.target.onerror = handleSecondThumbnailError} data-id="element-324" />}
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity duration-200" data-id="element-325">
            <button onClick={togglePlay} className="bg-red-600 hover:bg-red-700 rounded-full p-4 transition-transform hover:scale-110" aria-label="Play video" data-id="element-326">
              <PlayIcon size={24} className="text-white" data-id="element-327" />
            </button>
          </div>
          {embedAttempted && embedError && <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 p-1.5 rounded-md flex items-center" data-id="element-328">
              {errorType === 'embedding' ? <CopyrightIcon size={14} className="text-red-500 mr-1" data-id="element-329" /> : <AlertCircleIcon size={14} className="text-red-500 mr-1" data-id="element-330" />}
              <span className="text-xs text-white" data-id="element-331">Can't embed</span>
            </div>}
          {duration && <div className="absolute bottom-1 right-1 bg-black bg-opacity-80 px-1 py-0.5 rounded text-xs font-medium" data-id="element-332">
              {duration}
            </div>}
        </>;
    }
  };
  return <div className="relative group" data-id="element-333">
      {isCustom && <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-334">
          <button onClick={onRemove} className="bg-red-600 hover:bg-red-700 p-1 rounded-full" aria-label="Remove custom video" data-id="element-335">
            <TrashIcon size={16} data-id="element-336" />
          </button>
        </div>}
      <div className="cursor-pointer" data-id="element-337">
        <div className="relative rounded-xl overflow-hidden aspect-video bg-gray-800" onClick={isPlaying || isThumbnailOnly ? null : togglePlay} data-id="element-338">
          {renderVideoContent()}
          {/* Video controls that show when playing and not in error state */}
          {isPlaying && !embedError && !isLoading && !isThumbnailOnly && <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-center justify-between" data-id="element-339">
              <button onClick={togglePlay} className="bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full p-1" aria-label={isPlaying ? 'Pause video' : 'Play video'} data-id="element-340">
                {isPlaying ? <PauseIcon size={16} className="text-white" data-id="element-341" /> : <PlayIcon size={16} className="text-white" data-id="element-342" />}
              </button>
              <button onClick={toggleMute} className="bg-black bg-opacity-70 hover:bg-opacity-90 rounded-full p-1" aria-label={isMuted ? 'Unmute video' : 'Mute video'} data-id="element-343">
                {isMuted ? <VolumeXIcon size={16} className="text-white" data-id="element-344" /> : <Volume2Icon size={16} className="text-white" data-id="element-345" />}
              </button>
            </div>}
        </div>
        <div className="mt-3 flex" data-id="element-346">
          <div className="w-9 h-9 rounded-full bg-gray-600 flex-shrink-0 overflow-hidden" data-id="element-347">
            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold" data-id="element-348">
              {channelName ? channelName.charAt(0).toUpperCase() : 'Y'}
            </div>
          </div>
          <div className="ml-3 flex-1 overflow-hidden" data-id="element-349">
            {isEditing ? <input ref={inputRef} type="text" value={editedTitle} onChange={e => setEditedTitle(e.target.value)} onKeyDown={handleKeyDown} onBlur={handleTitleSave} className="w-full bg-[#121212] border border-[#303030] rounded-md py-1 px-2 text-sm font-medium leading-[1.2rem] max-h-[2.4rem] focus:outline-none focus:border-blue-500" maxLength={100} data-id="element-350" /> : <h3 className={`font-medium line-clamp-2 text-sm leading-[1.2rem] max-h-[2.4rem] overflow-hidden ${isCustom && onTitleUpdate ? 'hover:bg-[#272727] hover:cursor-text px-2 py-1 -ml-2 rounded' : ''}`} onClick={handleTitleClick} title={isCustom && onTitleUpdate ? 'Click to edit title' : title} data-id="element-351">
                {editedTitle}
              </h3>}
            <p className="text-[#AAAAAA] text-xs mt-1 flex items-center truncate" data-id="element-352">
              {channelName || 'Channel name'}
              {channelVerified && <CheckCircleIcon size={12} className="ml-1 text-[#AAAAAA] flex-shrink-0" data-id="element-353" />}
            </p>
            <div className="flex text-[#AAAAAA] text-xs" data-id="element-354">
              <span className="truncate" data-id="element-355">{views || '0 views'}</span>
              <span className="mx-1 flex-shrink-0" data-id="element-356">•</span>
              <span className="truncate" data-id="element-357">{timestamp || 'Just now'}</span>
            </div>
          </div>
          <div className="group-hover:visible invisible ml-1" data-id="element-358">
            <button className="p-1 rounded-full hover:bg-[#272727]" data-id="element-359">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" data-id="element-360">
                <path d="M12 16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zM10.5 12c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5zm0-6c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5-.67-1.5-1.5-1.5-1.5.67-1.5 1.5z" data-id="element-361"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>;
};